import React, { forwardRef } from 'react';
import { LifeInWeeksGrid, calculateWeeksLived, calculateNextBirthdayAge } from './LifeInWeeksGrid';

export const PrintableCardV2 = forwardRef(({ 
  birthDate, 
  recipientName,
  inspirationalQuote = "The present moment is filled with joy and happiness. If you are attentive, you will see it.",
  quoteAuthor = "Thich Nhat Hanh",
  accentColor = '#000000'
}, ref) => {
  const weeksLived = calculateWeeksLived(birthDate);
  const weeksRemaining = 4680 - weeksLived;
  const age = calculateNextBirthdayAge(birthDate);

  return (
    <div ref={ref} className="bg-white">
      {/* 
        A4 Landscape: 297mm × 210mm
        Three equal panels: 99mm each
        Panel order (left to right): Cover | Message | Grid
      */}
      <div 
        className="relative bg-white"
        style={{ 
          width: '297mm', 
          height: '210mm'
        }}
        data-testid="printable-card-v2"
      >
        {/* ============================================
            PANEL 1 - LEFT (0-99mm): FRONT COVER
            ============================================ */}
        <div 
          className="absolute left-0 top-0 h-full flex flex-col items-center justify-center"
          style={{ 
            width: '99mm', 
            padding: '5mm'
          }}
          data-testid="panel-cover"
        >
          <div className="text-center">
            {/* Large weeks ahead number */}
            <p 
              className="leading-none"
              style={{ 
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontWeight: 700,
                fontSize: '72px',
                color: '#1A1A1A'
              }}
            >
              {weeksRemaining > 0 ? weeksRemaining.toLocaleString() : '0'}
            </p>
            
            {/* "WEEKS AHEAD" */}
            <p 
              className="uppercase tracking-[0.2em]"
              style={{ 
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '18px',
                fontWeight: 400,
                color: '#1A1A1A',
                marginTop: '10mm'
              }}
            >
              Weeks Ahead
            </p>
            
            {/* Small italic */}
            <p 
              className="italic"
              style={{ 
                fontFamily: "'Georgia', serif",
                fontSize: '10px',
                color: '#9A9A9A',
                marginTop: '5mm'
              }}
            >
              (If you live a rich life through to 90)
            </p>
            
            {/* Inspirational Quote */}
            <div 
              className="w-full px-2"
              style={{ marginTop: '12mm', marginBottom: '12mm', maxWidth: '85mm' }}
            >
              <div className="w-10 h-px bg-[#E5E5E5] mx-auto mb-3" />
              <p 
                className="italic text-center"
                style={{ 
                  fontFamily: "'Georgia', serif",
                  fontSize: '11px',
                  lineHeight: '1.5',
                  color: '#333333'
                }}
              >
                "{inspirationalQuote}"
              </p>
              {quoteAuthor && (
                <p 
                  className="text-center mt-2"
                  style={{ 
                    fontFamily: "'Georgia', serif",
                    fontSize: '9px',
                    color: '#737373'
                  }}
                >
                  — {quoteAuthor}
                </p>
              )}
              <div className="w-10 h-px bg-[#E5E5E5] mx-auto mt-3" />
            </div>
            
            {/* Recipient Name */}
            <h1 
              style={{ 
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '36px',
                fontWeight: 400,
                color: '#1A1A1A',
                letterSpacing: '0.08em'
              }}
            >
              {recipientName ? recipientName.toUpperCase() : 'DEAR FRIEND'}
            </h1>
            
            {/* Age - closer to name */}
            <p 
              style={{ 
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '48px',
                fontWeight: 400,
                color: accentColor,
                marginTop: '3mm'
              }}
            >
              {age}
            </p>
          </div>
        </div>

        {/* ============================================
            PANEL 2 - MIDDLE (99-198mm): MESSAGE SPACE
            Completely blank for handwritten message
            ============================================ */}
        <div 
          className="absolute top-0 h-full"
          style={{ 
            left: '99mm', 
            width: '99mm',
            padding: '5mm'
          }}
          data-testid="panel-message"
        >
          {/* Intentionally blank for handwriting */}
        </div>

        {/* ============================================
            PANEL 3 - RIGHT (198-297mm): GRID REVEAL
            ============================================ */}
        <div 
          className="absolute right-0 top-0 h-full flex flex-col items-center justify-center"
          style={{ 
            width: '99mm', 
            padding: '5mm'
          }}
          data-testid="panel-grid"
        >
          <div className="text-center w-full flex flex-col items-center">
            {/* Title */}
            <p 
              className="uppercase tracking-[0.2em] mb-3"
              style={{ 
                fontFamily: "'Playfair Display', 'Georgia', serif",
                fontSize: '10px',
                color: '#737373'
              }}
            >
              Your Life in Weeks
            </p>
            
            {/* The Grid - max 89mm wide with 5mm margins each side */}
            <div 
              className="mb-4 flex justify-center"
              style={{ 
                border: '1px solid #1A1A1A',
                padding: '2mm',
                maxWidth: '89mm'
              }}
            >
              <LifeInWeeksGrid 
                birthDate={birthDate} 
                compact={true}
                showLabels={false}
                filledColor={accentColor}
                emptyColor="#E5E5E5"
              />
            </div>

            {/* Statistics Section - moved up */}
            <div className="space-y-0" style={{ marginTop: '1mm' }}>
              <p 
                style={{ 
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: accentColor
                }}
              >
                {weeksLived.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: 400, color: '#737373' }}>weeks lived</span>
              </p>
              <p 
                style={{ 
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#737373'
                }}
              >
                {weeksRemaining.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: 400 }}>weeks ahead</span>
              </p>
              <p 
                style={{ 
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                  fontSize: '14px',
                  fontWeight: 500,
                  fontStyle: 'italic',
                  color: accentColor,
                  marginTop: '8mm'
                }}
              >
                Make them count.
              </p>
            </div>
          </div>
        </div>

        {/* Fold Lines - at 99mm and 198mm - darker */}
        <div 
          className="absolute top-0 bottom-0 border-l border-dashed"
          style={{ left: '99mm', borderColor: '#888888', borderWidth: '1px' }}
        />
        <div 
          className="absolute top-0 bottom-0 border-l border-dashed"
          style={{ left: '198mm', borderColor: '#888888', borderWidth: '1px' }}
        />
      </div>
    </div>
  );
});

PrintableCardV2.displayName = 'PrintableCardV2';

export default PrintableCardV2;
